import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// Lazy-initialised Supabase client for the backoffice, mirroring the
// dashboard's (apps/dashboard/src/lib/supabase.ts). The factory only reads env
// vars and constructs the client on first call, so importing this module stays
// safe in tests — they never touch the singleton.
//
// A DISTINCT `storageKey` ('nuberu-admin-auth') keeps the backoffice session
// separate from the operator dashboard's, so being signed into one is never
// implicitly being signed into the other, even on the same browser.
//
// Anon/publishable-key access is fine: it only authenticates the auth-domain
// calls themselves. Authorisation for the backoffice is enforced server-side by
// the API's platform-admin allowlist — the publishable key grants nothing.

let cached: SupabaseClient | null = null;

export const getSupabaseClient = (): SupabaseClient => {
  if (cached !== null) return cached;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !publishableKey) {
    throw new Error(
      'Backoffice configuration missing — set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in .env.',
    );
  }

  cached = createClient(url, publishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'nuberu-admin-auth',
      flowType: 'pkce',
    },
  });
  return cached;
};
